/**
 * Floating Video Widget
 * Handles scroll/time triggers, autoplay, volume control, and minimize/restore.
 * Closing the widget only minimizes it to a small help icon — it is never permanently dismissed.
 */

(function() {
  'use strict';

  class FloatingVideoWidget {
    constructor(widget) {
      this.widget = widget;
      this.helpIcon = document.getElementById('floating_video_help_icon');
      this.triggerMethod = widget.dataset.triggerMethod || 'scroll';
      this.triggerValue = parseInt(widget.dataset.triggerValue) || 50;
      this.loop = widget.dataset.loop === 'true';

      this.video = widget.querySelector('.floating-video-element');
      this.closeBtn = widget.querySelector('[data-widget-close]');
      this.volumeBtn = widget.querySelector('[data-volume-toggle]');

      this.hasBeenShown = false;
      this.storageKey = 'fvw_minimized';
      this.storageDateKey = 'fvw_minimized_date';

      // Single-overlay coordination (see assets/overlay-manager.js)
      this.overlayId = 'floating-video-widget';
      this.overlayPriority = 20;

      this.attachEventListeners();
      this.attachHelpIconListener();

      // If minimized today, show help icon; otherwise reset and run trigger (widget opens again)
      if (this.wasMinimizedToday()) {
        this.showHelpIcon();
        this.overlayCancel();
      } else {
        this.clearMinimizedState();
        this.setupTrigger();
      }
    }

    getTodayDateString() {
      const d = new Date();
      return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0');
    }

    wasMinimizedToday() {
      if (localStorage.getItem(this.storageKey) !== 'true') return false;
      const stored = localStorage.getItem(this.storageDateKey);
      return stored === this.getTodayDateString();
    }

    clearMinimizedState() {
      localStorage.removeItem(this.storageKey);
      localStorage.removeItem(this.storageDateKey);
    }

    setupTrigger() {
      if (this.triggerMethod === 'scroll') {
        this.setupScrollTrigger();
      } else if (this.triggerMethod === 'time') {
        this.setupTimeTrigger();
      } else {
        this.requestShow();
      }
    }

    setupScrollTrigger() {
      const scrollHandler = () => {
        if (this.hasBeenShown) return;
        const totalScroll = document.documentElement.scrollHeight - window.innerHeight;
        const scrollPercent = totalScroll > 0 ? (window.scrollY / totalScroll) * 100 : 100;
        if (scrollPercent >= this.triggerValue) {
          this.requestShow();
          window.removeEventListener('scroll', scrollHandler);
        }
      };
      window.addEventListener('scroll', scrollHandler, { passive: true });
      scrollHandler();
    }

    setupTimeTrigger() {
      setTimeout(() => {
        this.requestShow();
      }, this.triggerValue * 1000);
    }

    // ── Overlay manager shims (no-ops if overlay-manager.js is absent) ──
    overlayRequest(opts) {
      if (!window.KraftOverlays) return true;
      return window.KraftOverlays.request(this.overlayId, opts);
    }

    overlayRelease() {
      if (window.KraftOverlays) window.KraftOverlays.release(this.overlayId);
    }

    overlayCancel() {
      if (window.KraftOverlays) window.KraftOverlays.cancel(this.overlayId);
    }

    // Auto-trigger path: only show if no other overlay owns the screen.
    // If something else is up, we queue and slide in once it clears.
    requestShow() {
      if (this.hasBeenShown) return;
      const granted = this.overlayRequest({
        priority: this.overlayPriority,
        show: () => this.showWidget()
      });
      if (granted) this.showWidget();
    }

    showWidget() {
      this.hasBeenShown = true;
      this.widget.classList.remove('hidden');
      this.hideHelpIcon();
      if (this.video) {
        this.video.play().catch(() => {});
      }
    }

    hideWidget() {
      this.widget.classList.add('hidden');
      if (this.video && !this.video.paused) {
        this.video.pause();
      }
      this.overlayRelease();
    }

    showHelpIcon() {
      if (this.helpIcon) {
        setTimeout(() => this.helpIcon.classList.remove('hidden'), 400);
      }
    }

    hideHelpIcon() {
      if (this.helpIcon) {
        this.helpIcon.classList.add('hidden');
      }
    }

    attachHelpIconListener() {
      if (!this.helpIcon) return;
      this.helpIcon.addEventListener('click', () => {
        this.clearMinimizedState();
        this.hideHelpIcon();
        this.overlayRequest({ queue: false });
        this.showWidget();
        if (this.video) {
          this.video.currentTime = 0;
          this.video.play().catch(() => {});
        }
      });
    }

    attachEventListeners() {
      if (this.closeBtn) {
        this.closeBtn.addEventListener('click', () => {
          this.hideWidget();
          localStorage.setItem(this.storageKey, 'true');
          localStorage.setItem(this.storageDateKey, this.getTodayDateString());
          this.showHelpIcon();
        });
      }

      if (this.volumeBtn && this.video) {
        this.volumeBtn.addEventListener('click', () => {
          this.video.muted = !this.video.muted;
          this.volumeBtn.classList.toggle('muted', this.video.muted);
        });
      }
    }
  }

  function initWidget() {
    const widget = document.getElementById('floating_video_widget');
    if (widget) {
      const instance = new FloatingVideoWidget(widget);
      widget._floatingWidget = instance;
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initWidget);
  } else {
    initWidget();
  }

  window.showFloatingVideoWidget = function() {
    const widget = document.getElementById('floating_video_widget');
    if (widget && widget._floatingWidget) widget._floatingWidget.showWidget();
  };

  window.hideFloatingVideoWidget = function() {
    const widget = document.getElementById('floating_video_widget');
    if (widget && widget._floatingWidget) widget._floatingWidget.hideWidget();
  };
})();
